import React, { useState, useEffect, useCallback, useRef } from 'react';
import CommonInput from '../../Common/CommonInput';
import CommonIconRadio from '../../Common/CommonIconRadio';
import CommonDate from '../../Common/CommonDate';
import CommonTextarea from '../../Common/CommonTextarea';


const PaymentForm = ({ initialData, onSave, isLoading, onClose, customActionsDispatch = {} }) => {

    const [formData, setFormData] = useState({
        id:  '',
        net_total: '',
        advance_payment: '',
        payment: '',
        balance: '',
        payment_type: '',
        payment_date:  new Date().toISOString().split('T')[0],
        payment_amount: '',
        payment_reference: '',

        payments: []
    });
    const [errors, setErrors] = useState({});
    const [isSaving, setIsSaving] = useState(false);

    // Calculate total payments and balance
    const calculateTotals = useCallback(() => {
        const totalPayments = formData.payments.reduce((sum, payment) => {
            return sum + (parseFloat(payment.payment_amount) || 0);
        }, 0);

        const netTotal = parseFloat(formData.net_total) || 0;
        const advancePayment = parseFloat(formData.advance_payment) || 0;
        const balance = netTotal - advancePayment - totalPayments;

        setFormData(prev => ({
            ...prev,
            payment: totalPayments.toFixed(3),
            balance: balance.toFixed(3)
        }));
    }, [formData.payments, formData.net_total, formData.advance_payment]);

    useEffect(() => {
        calculateTotals();
    }, [calculateTotals]);

    useEffect(() => {
        if (initialData) {
            setFormData({
                ...initialData,
                id: initialData.id || '',
                net_total: (parseFloat(initialData.net_total) || 0).toFixed(3),
                advance_payment: (parseFloat(initialData.advance_payment) || 0).toFixed(3),
                payment: '',
                balance: ((parseFloat(initialData.net_total) || 0) - (parseFloat(initialData.advance_payment) || 0)).toFixed(3),

                payment_type: initialData?.payment_type || '',
                payment_date: initialData?.payment_date || new Date().toISOString().split('T')[0],
                payment_amount: initialData?.payment_amount ? (parseFloat(initialData.payment_amount) || 0).toFixed(3) : '',
                payment_reference: initialData?.payment_reference || '',
                payments: initialData.payments ? initialData.payments.map(payment => ({
                    ...payment,
                    payment_amount: (parseFloat(payment.payment_amount) || 0).toFixed(3)
                })) : []
            });
        }
    }, [initialData]);

    const handleAddPayment = () => {
        // Validate payment type
        if (!formData.payment_type) {
            setErrors(prev => ({
                ...prev,
                payment_type: 'Please select a payment type'
            }));
            return;
        }

        // Validate payment amount
        if (!formData.payment_amount || parseFloat(formData.payment_amount) <= 0) {
            setErrors(prev => ({
                ...prev,
                payment_amount: 'Payment amount must be greater than 0'
            }));
            return;
        }

        // Validate payment amount against balance
        const currentBalance = parseFloat(formData.balance) || 0;
        const paymentAmount = parseFloat(formData.payment_amount) || 0;
        if (paymentAmount > currentBalance) {
            setErrors(prev => ({
                ...prev,
                payment_amount: `Payment amount cannot exceed remaining balance of ${currentBalance.toFixed(3)}`
            }));
            return;
        }

        setFormData(prev => ({
            ...prev,
            payment_type: '', 
            payment_amount: '', 
            payment_date: new Date().toISOString().split('T')[0], 
            payment_reference: '',
            payments: [...prev.payments, { 
                payment_type: prev.payment_type, 
                payment_amount: parseFloat(prev.payment_amount).toFixed(3), 
                payment_date: prev.payment_date, 
                payment_reference: prev.payment_reference 
            }]
        }));

        // Clear any existing errors after successful addition
        setErrors(prev => ({
            ...prev,
            payment_type: '',
            payment_amount: ''
        }));
    };

    const handleDeletePayment = (index) => {
        setFormData(prev => ({
            ...prev,
            payments: prev.payments.filter((_, i) => i !== index)
        }));
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        // Auto-fill payment_amount with balance when payment type is selected
        if (name === 'payment_type' && value) {
            setFormData(prev => ({
                ...prev,
                [name]: value,
                payment_amount: parseFloat(prev.balance).toFixed(3) // Auto-fill with balance
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            }));
        }

        // Validate payment amount against balance when it changes
        if (name === 'payment_amount') {
            const currentBalance = parseFloat(formData.balance) || 0;
            const paymentAmount = parseFloat(value) || 0;
            if (paymentAmount > currentBalance) {
                setErrors(prev => ({
                    ...prev,
                    payment_amount: `Payment amount cannot exceed remaining balance of ${currentBalance.toFixed(3)}`
                }));
            } else if (errors.payment_amount) {
                setErrors(prev => ({...prev, payment_amount: ''}));
            }
        } else if (errors[name]) {
            setErrors(prev => ({...prev, [name]: ''}));
        }
    };


    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setIsSaving(true);
            await onSave(formData);
            setErrors({});
            onClose();
        } catch (error) {
            if (error.errors) {
                const newErrors = {};
                Object.keys(error.errors).forEach(key => {
                    newErrors[key] = Array.isArray(error.errors[key]) 
                        ? error.errors[key][0] 
                        : error.errors[key];
                });
                setErrors(newErrors);
            } else {
                setErrors({
                    name: error.message || 'An error occurred while saving'
                });
            }
            return;
        } finally {
            setIsSaving(false);
        }
    };

    const isSubmitting = isLoading || isSaving;

    return (
        <form onSubmit={handleSubmit} className="h-100 d-flex flex-column">
            <div className="flex-grow-1">
                <div className="row">
                    <div className="col-md-12">
                        <div className="mb-3">
                            <label className="form-label">Net Total</label>
                            <CommonInput
                                type="number"
                                name="net_total"
                                onChange={() => {}}
                                disabled={true}
                                value={formData.net_total}
                                errors={errors}
                            />
                        </div>
                    </div>
                    <div className="col-md-12">
                        <div className="mb-3">
                            <label className="form-label">Advance Paid</label>
                            <CommonInput
                                type="number"
                                name="advance_payment"
                                onChange={() => {}}
                                disabled={true}
                                value={formData.advance_payment}
                                errors={errors}
                            />
                        </div>
                    </div>
                    <div className="col-md-12">
                        <div className="mb-3">
                            <label className="form-label">Balance</label>
                            <CommonInput
                                type="number"
                                name="balance"
                                onChange={() => {}}
                                disabled={true}
                                value={formData.balance}
                                errors={errors}
                            />
                        </div>
                    </div>
                    <div className="col-md-12">
                        <div className="mb-3">
                            <label className="form-label">Payment</label>
                            <CommonInput
                                type="number"
                                name="payment"
                                onChange={handleChange}
                                disabled={true}
                                value={formData.payment}
                                errors={errors}
                            />
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-12">
                        <div className="table-responsive mb-2">
                            <table className="table table-bordered">
                                <thead>
                                    <tr>
                                        <th>Type</th>
                                        <th>Amount</th>
                                        <th>Date</th>
                                        <th>Reference</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {formData?.payments && formData.payments.length > 0 ? (
                                        formData.payments.map((payment, index) => (
                                            <tr key={index}>
                                                <td>{payment.payment_type}</td>
                                                <td>{payment.payment_amount}</td>
                                                <td>{payment.payment_date}</td>
                                                <td>{payment.payment_reference}</td>
                                                <td>
                                                    <button
                                                        type="button"
                                                        className="btn btn-danger btn-sm"
                                                        onClick={() => handleDeletePayment(index)}
                                                        disabled={isSubmitting}
                                                    >
                                                        <i className="fa fa-trash"></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="5" className="text-center">No payments added</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
                <div className='row'>
                    <div className='col-md-12'>
                        <div className='row justify-content-center'>
                            <div className='col col-sm-4 col-md-3 col-xxl-4 mb-4'>
                                <CommonIconRadio
                                    name="payment_type"
                                    id="payment_type1"
                                    value="cash"
                                    label="Cash"
                                    leftIcon="fa fa-money-bill"
                                    onChange={handleChange}
                                    checked={formData?.payment_type === 'cash'}
                                    disabled={isSubmitting}
                                    errors={errors}
                                    tickContainerClassName="radio_tick_container"
                                />
                            </div>
                            <div className='col col-sm-4 col-md-3 col-xxl-4 mb-4'>
                                <CommonIconRadio
                                    name="payment_type"
                                    id="payment_type2"
                                    value="bank"
                                    label="Bank"  
                                    leftIcon="fa fa-bank"
                                    onChange={handleChange}
                                    checked={formData?.payment_type === 'bank'}
                                    disabled={isSubmitting}
                                    errors={errors}
                                    tickContainerClassName="radio_tick_container"
                                />
                            </div>
                            <div className='col col-sm-4 col-md-3 col-xxl-4 mb-4'>
                                <CommonIconRadio
                                    name="payment_type"
                                    id="payment_type3"
                                    value="card"
                                    label="Card"
                                    leftIcon="fa fa-credit-card"
                                    onChange={handleChange}
                                    checked={formData?.payment_type === 'card'}
                                    disabled={isSubmitting}
                                    errors={errors}
                                    tickContainerClassName="radio_tick_container"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="col-md-6">
                        <div className="mb-3">
                            <label className="form-label">Payment Amount</label>
                            <CommonInput
                                type="number"
                                name="payment_amount"
                                onChange={handleChange}
                                disabled={isSubmitting}
                                value={formData.payment_amount}
                                errors={errors}
                            />
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="mb-3">
                            <label className="form-label">Payment Date</label>
                            <CommonDate
                                type="date"
                                name="payment_date"
                                onChange={handleChange}
                                disabled={isSubmitting}
                                value={formData.payment_date}
                                errors={errors}
                            />
                        </div>
                    </div>
                    <div className="col-md-12">
                        <div className="mb-3">
                            <label className="form-label">Payment Reference</label>
                            <CommonTextarea
                                type="text"
                                rows={2}
                                name="payment_reference"
                                onChange={handleChange}
                                disabled={isSubmitting}
                                value={formData.payment_reference}
                                errors={errors}
                            />
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="col-md-12">
                        <button type="button" className="btn btn-warning" onClick={handleAddPayment}>Add Payment</button>
                    </div>
                </div>


            </div>
            <div className="form-footer">
                <button 
                    type="button" 
                    className="btn btn-light" 
                    onClick={onClose}
                    disabled={isSubmitting}
                >
                    Cancel
                </button>
                <button 
                    type="submit" 
                    className="btn btn-primary" 
                    disabled={isSubmitting}
                >
                    {isSubmitting ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Saving...
                        </>
                    ) : 'Save'}
                </button>
            </div>
        </form>
    );
};

export default PaymentForm; 